import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { request } from "shared/api/request"
import { ExportsAPIType, serializer } from "shared/api/serializer/serializer"
import {
  ATTENTION_TOAST_TIMEOUT,
  DEFAULT_TOAST_TIMEOUT
} from "shared/constants/app-constants"
import { repeatUntil } from "shared/lib/repeatUntil"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { FileExport } from "shared/types/Export"
import {
  ProjectPDFGeneralDataOption,
  ProjectPDFInstallationReferencesOption,
  ProjectPDFPriceOption,
  ProjectPDFRoomDataOption
} from "shared/types/PDFExport"
import { ToastType } from "shared/types/Toast"

import { ProjectPDFOverlayFormData } from "widgets/project/project-pdf-overlay/ProjectPDFOverlay"

enum APIProjectPDFPriceOption {
  PRICES_ONLY = "prices-only",
  PRICES_AND_GROUPS = "prices-and-groups"
}

export interface ProjectPDFExportOptions {
  remark?: boolean
  description?: boolean
  dimensioning?: boolean
  deliveryTime?: boolean

  roomDimensions: boolean
  roomAirData: boolean
  roomRemark: boolean

  price?: APIProjectPDFPriceOption
  installationReferencesImages?: boolean
  installationReferencesQRCode?: boolean
}

function mapPriceOption(
  option: ProjectPDFPriceOption
): APIProjectPDFPriceOption | undefined {
  switch (option) {
    case ProjectPDFPriceOption.PRICES_AND_GROUPS:
      return APIProjectPDFPriceOption.PRICES_AND_GROUPS
    case ProjectPDFPriceOption.PRICES_ONLY:
      return APIProjectPDFPriceOption.PRICES_ONLY
    default:
      return undefined
  }
}

export function useProjectPDFExport(projectID: string, isShareID = false) {
  const { t } = useTranslation()

  const fetchProjectPDFData = useEvent(
    async (formData: ProjectPDFOverlayFormData): Promise<FileExport> => {
      const options: ProjectPDFExportOptions = {
        description: formData.position.includes(
          ProjectPDFGeneralDataOption.PRODUCT_DESCRIPTION
        ),
        dimensioning: formData.position.includes(
          ProjectPDFGeneralDataOption.DIMENSIONING_DATA
        ),
        remark: formData.position.includes(ProjectPDFGeneralDataOption.REMARKS),
        deliveryTime: formData.position.includes(
          ProjectPDFGeneralDataOption.DELIVERY_TIME
        ),
        installationReferencesImages:
          formData.installationReference ===
            ProjectPDFInstallationReferencesOption.IMAGES ||
          formData.installationReference ===
            ProjectPDFInstallationReferencesOption.QR_CODE_AND_IMAGE,
        installationReferencesQRCode:
          formData.installationReference ===
            ProjectPDFInstallationReferencesOption.QR_CODE ||
          formData.installationReference ===
            ProjectPDFInstallationReferencesOption.QR_CODE_AND_IMAGE,
        roomDimensions: formData.room.includes(
          ProjectPDFRoomDataOption.DIMENSIONS_DATA
        ),
        roomAirData: formData.room.includes(ProjectPDFRoomDataOption.AIR_DATA),
        roomRemark: formData.room.includes(ProjectPDFRoomDataOption.REMARK)
      }

      const priceOption = mapPriceOption(formData.price)

      if (priceOption) {
        options.price = priceOption
      }

      const fetchUrl = `${import.meta.env.VITE_API_BASE}/project-list${
        isShareID ? "/share" : ""
      }/projects/${projectID}/export/pdf?${new URLSearchParams(
        options as unknown as Record<string, string>
      )}`

      return request(fetchUrl, {
        headers: {
          Accept: "application/*"
        }
      }).then(response =>
        response.json().then(json => {
          return serializer.deserialize(ExportsAPIType, json)
        })
      )
    }
  )

  const handleExport = useEvent(async (formData: ProjectPDFOverlayFormData) => {
    const loadingToast = raiseToast({
      type: ToastType.LOADING,
      message: t("ProjectExport.Loading"),
      autoClose: false
    })

    function handleSuccess() {
      toast.update(loadingToast, {
        autoClose: DEFAULT_TOAST_TIMEOUT,
        data: {
          type: ToastType.SUCCESS,
          message: t("ProjectExport.Success")
        }
      })
    }

    function handleFailure(uiMessage: string | null) {
      toast.update(loadingToast, {
        autoClose: DEFAULT_TOAST_TIMEOUT,
        data: {
          type: ToastType.ERROR,
          timeout: ATTENTION_TOAST_TIMEOUT,
          message: `${t("ProjectExport.Failure")} ${uiMessage}`
        }
      })
    }

    await repeatUntil(
      () => fetchProjectPDFData(formData),
      (result: FileExport) => result.isComplete,
      (result: FileExport) => {
        if (result.url) {
          const tempLink = document.createElement("a")

          tempLink.href = result.url
          tempLink.click()
          tempLink.remove()

          handleSuccess()
          return Promise.resolve()
        }
        return Promise.reject(new Error("No Download URL given."))
      },
      {
        initialInterval: 1000,
        timeout: null,
        failurePredicateFn: (result: FileExport) => result.error?.message
      }
    ).promise.catch(handleFailure)
  })

  return {
    exportProject: handleExport
  }
}
